interface SubmitFormOptions {
  /** Power Automate or same-origin Azure Functions API endpoint URL */
  apiEndpoint: string;
  /** Form payload to send (already transformed, email→mail etc.) */
  payload: Record<string, any>;
  /**
   * Value of the "identity" field to check for [DEBUG] mode.
   * When present, the API call is skipped and success is simulated after 1s.
   */
  debugFieldValue?: string;
}

interface SubmitFormResult {
  success: boolean;
  error?: string;
}

/**
 * Submits form data to a form API endpoint.
 *
 * Shared infrastructure across all form pages:
 * - [DEBUG] mode: if debugFieldValue contains "[DEBUG]", skips the API call
 * - Automatically appends the ?source= URL parameter if present
 * - Parses the standardized { status, error, details } response
 */
export async function submitFormToApi(options: SubmitFormOptions): Promise<SubmitFormResult> {
  const { apiEndpoint, payload, debugFieldValue } = options;

  // Debug mode: skip API call, simulate a 1-second delay, then report success
  if (debugFieldValue?.includes('[DEBUG]')) {
    console.log('DEBUG mode: Skipping API call, simulating success');
    await new Promise(resolve => setTimeout(resolve, 1000));
    return { success: true };
  }

  // Forward the ?source= tracking parameter to the backend if present
  const searchParams = new URLSearchParams(window.location.search);
  const sourceParam = searchParams.get('source');
  const finalPayload = { ...payload };
  if (sourceParam) {
    finalPayload.source = sourceParam;
  }

  const response = await fetch(apiEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(finalPayload),
  });

  const responseData = await response.json();

  if (response.ok && responseData.status === 'success') {
    return { success: true };
  }

  // Build a user-facing error message from the API response
  const error = responseData.error
    ? `${responseData.error}${responseData.details ? `: ${responseData.details}` : ''}`
    : 'E-Mail-Validierung fehlgeschlagen. Bitte überprüfe Deine E-Mail-Adresse.';

  return { success: false, error };
}
